import { createSlice } from '@reduxjs/toolkit';
import {
  getBasketsbyDateRangeThunk,
  getInventoryDataForSubstitutionThunk,
  getOrderDetailsByIdThunk,
} from './OrderDetailsActions';
import { IBasket } from '../../OrderEntryForm/OrderEntrySlice';
import { holdInventoryDataThunk } from '../../../pages/SwapBaskets/SwapBasketsActions';
import { ArrayUtil } from 'helper-util';
import {
  addNewRowStatus,
  appendSubstitutionHoldIds,
  enrichBasketItemsFromRolledItems,
  getSubstitutionHoldIds,
  isCurrentSubstitutionHold,
  rehydrateSubstitutionHoldIdsFromBasket,
} from './OrderDetailsHelper';
import { v4 as uuidv4 } from 'uuid';

export interface IOrderDetails {
  clientId: string;
  counterParty: string;
  groupId: string;
  id: string;
  maturityDate: string;
  settlementDate: string;
  status: string;
  swapAttributes: string;
  swapVersion: string;
  swapId: string;
  tenantId: string;
  terminationDate: string;
  timestamp: string;
  tradeDate: string;
  swapName: string;
  currentBasket: IBasket;
  message?: string;
}

export interface ISecurity {
  ticker: string;
  securityName: string;
  cusip: string;
  isin: string;
  sedol: string;
  issueCurrency: string;
  bnySecurityId: string;
  assetClass: string;
}
export interface IPositionSecurity {
  asOfTimestamp: string;
  assetType: string;
  availableQuantity: number;
  bnySecurityId: string;
  fundId: string;
  fxRate: number;
  indicativeRateFee: number;
  indicativeRateRebate: number;
  inputCurrency: string;
  location: string;
  marketValueAvailable: number;
  onLoanMarketValue: number;
  onLoanQuantity: number;
  restricted: false;
  securityCusip: string;
  securityIsin: string;
  securityIssueCountry: string;
  securityMarketPrice: number;
  securityName: string;
  securitySedol: string;
  securityTicker: string;
  shortCode: string;
  specialness: string;
  totalMarketValue: number;
}

export interface OrderDetailsData {
  orderDetails: IOrderDetails;
  baskets: IBasket[];
  basket: IBasket;
  loading: boolean;
  basketVersion: number;
  swapVersion: number;
  holdObject: any;
  securityPositionMaster: IPositionSecurity[];
}

const initialState: OrderDetailsData = {
  orderDetails: {} as IOrderDetails,
  basket: {} as IBasket,
  baskets: null,
  loading: false,
  basketVersion: 1,
  swapVersion: 1,
  holdObject: {},
  securityPositionMaster: [],
};

const orderDetailsSlice = createSlice({
  name: 'Order Details',
  initialState,
  reducers: {
    deleteCancelledRow: (state, action) => {
      state.basket = {
        ...state.basket,
        // rollupItemDetails: {
        //   ...state.basket.rollupItemDetails,
        //   basketItems: state.basket.rollupItemDetails.basketItems.filter(
        //     (b) => b.basketItemId !== action.payload.id,
        //   ),
        // },
        consolidatedItems: {
          ...state.basket.consolidatedItems,
          basketItems: state.basket.consolidatedItems.basketItems.filter(
            (b) => b.basketItemId !== action.payload.id,
          ),
        },
      };
    },
    clearQuantitiesForCancelledRow: (state, action) => {
      state.basket = {
        ...state.basket,
        // rollupItemDetails: {
        //   ...state.basket.rollupItemDetails,
        //   basketItems: state.basket?.rollupItemDetails?.basketItems?.map((b) => {
        //     if (b.basketItemId === action.payload.id) {
        //       return { ...b, substitutionQuantity: 0, notional: 0 };
        //     } else {
        //       return b;
        //     }
        //   }),
        // },
        consolidatedItems: {
          ...state.basket.consolidatedItems,
          basketItems: state.basket?.consolidatedItems?.basketItems?.map((b) => {
            if (b.basketItemId === action.payload.id || b.uniqueId === action.payload.id) {
              return { ...b, substitutionQuantity: 0, hedgedNotional: 0 };
            } else {
              return b;
            }
          }),
        },
      };
    },
    resetHoldObject: (state) => {
      state.holdObject = {};
    },
    resetBaskets: (state) => {
      state.baskets = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderDetailsByIdThunk.pending, (state) => {
        state.loading = true;
        state.orderDetails = initialState.orderDetails;
        state.basket = initialState.basket;
      })
      .addCase(getOrderDetailsByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload?.data?.swap || {};
        const currentBasket = action.payload?.data?.swap?.currentBasket;
        const swapId = action.payload?.data?.swap?.id;
        const rehydratedHoldIds = rehydrateSubstitutionHoldIdsFromBasket(swapId, currentBasket);
        state.basket = {
          ...action.payload?.data?.swap?.currentBasket,
          // rollupItemDetails: {
          //   ...currentBasket.rollupItemDetails,
          //   basketItems:
          //     currentBasket?.rollupItemDetails?.basketItems?.map((bsktItm) => {
          //       return {
          //         ...bsktItm,
          //         hasBelongToCurrentBasket: isCurrentSubstitutionHold(
          //           bsktItm.tradeId,
          //           swapId,
          //           currentBasket.version,
          //           rehydratedHoldIds,
          //           bsktItm,
          //           currentBasket.id,
          //         ),
          //       };
          //     }) || [],
          // },
          consolidatedItems: {
            ...currentBasket.consolidatedItems,
            basketItems:
              currentBasket?.consolidatedItems?.basketItems?.map((bsktItm) => {
                return {
                  ...bsktItm,
                  uniqueId: uuidv4(),
                  tradeId: bsktItm.originalHoldId ?? null,
                  hasBelongToCurrentBasket: isCurrentSubstitutionHold(
                    bsktItm.tradeId,
                    swapId,
                    currentBasket.version,
                    rehydratedHoldIds,
                    bsktItm,
                    currentBasket.id,
                  ),
                };
              }) || [],
          },
        };
        // state.basket = action.payload?.data?.swap?.baskets
        //   ? (action.payload?.data?.swap?.baskets.find((b) => b.version === state.basketVersion) ??
        //     action.payload?.data?.swap?.baskets[0])
        //   : ({} as IBasket);
      })
      .addCase(getOrderDetailsByIdThunk.rejected, (state) => {
        state.loading = false;
        state.orderDetails = initialState.orderDetails;
        state.basket = initialState.basket;
      })
      .addCase(holdInventoryDataThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(holdInventoryDataThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.holdObject = action.payload;

        const fromSubstitution = action.meta.arg?.params?.fromSubstitution;
        const createdBasket = action.payload?.createdBasket;
        const swapId = createdBasket?.orderId ?? state.orderDetails?.id;
        const basketVersion = createdBasket?.version;
        const newHoldIds =
          action.payload?.results?.filter((rslt) => !!rslt.holdId)?.map((rslt) => rslt.holdId) ||
          [];

        let activeHoldIds = getSubstitutionHoldIds(swapId, basketVersion);
        if (fromSubstitution && !ArrayUtil.isEmpty(newHoldIds)) {
          activeHoldIds = appendSubstitutionHoldIds(swapId, basketVersion, newHoldIds);
        }

        const enrichedItems = enrichBasketItemsFromRolledItems(
          // createdBasket?.items,
          createdBasket?.consolidatedItems?.basketItems,
        );

        let updatedItems = enrichedItems;
        if (fromSubstitution) {
          updatedItems =
            enrichedItems?.map((item) => {
              // const hasNotBelongToCurrentBasket =
              //   createdBasket?.status === 'DRAFT_FOR_EDIT' &&
              //   item.substitutionQuantity !== 0 &&
              //   item.originalBasketId !== createdBasket?.id;
              return {
                ...item,
                // substitutionQuantity: hasNotBelongToCurrentBasket
                //   ? (item.quantity ?? 0)
                //   : (item.substitutionQuantity ?? 0),
                // quantity: hasNotBelongToCurrentBasket
                //   ? (item.substitutionQuantity ?? 0)
                //   : (item.quantity ?? 0),
                hasBelongToCurrentBasket: isCurrentSubstitutionHold(
                  item.tradeId,
                  swapId,
                  basketVersion,
                  activeHoldIds,
                  item,
                  createdBasket?.id,
                ),
              };
            }) || [];
        }

        const items = createdBasket
          ? (updatedItems ?? [])
          : (state.basket.consolidatedItems?.basketItems ?? []);

        state.basket = {
          ...state.basket,
          id: createdBasket?.id,
          version: createdBasket?.version,
          consolidatedItems: {
            ...state.basket?.consolidatedItems,
            basketItems: addNewRowStatus(items, activeHoldIds),
          },
        };
      })
      .addCase(holdInventoryDataThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getInventoryDataForSubstitutionThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getInventoryDataForSubstitutionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.securityPositionMaster = action.payload.items;
      })
      .addCase(getInventoryDataForSubstitutionThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getBasketsbyDateRangeThunk.pending, (state) => {
        state.loading = true;
        state.baskets = null;
      })
      .addCase(getBasketsbyDateRangeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.baskets = action.payload?.data?.baskets;
      })
      .addCase(getBasketsbyDateRangeThunk.rejected, (state) => {
        state.loading = false;
        state.baskets = null;
      });
  },
});

export const { deleteCancelledRow, clearQuantitiesForCancelledRow, resetHoldObject, resetBaskets } =
  orderDetailsSlice.actions;
export default orderDetailsSlice.reducer;
